import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  IconButton,
  Tooltip,
  Box,
  Input,
  FormControl,
  FormLabel,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAudioContext } from '../context/AudioContext';
import { useNotes, GLOBAL_CHARACTER_LIMIT } from '../context/NotesContext';
import { useDisplayNames } from '../context/DisplayNamesContext';
import { FaPlay, FaPause, FaFastForward, FaFastBackward, FaStepForward, FaStepBackward } from 'react-icons/fa';
import WaveSurfer from 'wavesurfer.js';
import { isIOS, isMobileDevice } from '../utils/deviceDetection';
import TooltipKeyBox from './TooltipKeyBox';

// Extend Window interface for auto-insert timestamp and chord flags
declare global {
  interface Window {
    __autoInsertTimestamp?: boolean;
    __autoInsertChord?: boolean;
  }
}

interface NotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackNumber: number;
  fileId?: string;
}

const NotesModal: React.FC<NotesModalProps> = ({ isOpen, onClose, trackNumber, fileId }) => {
  const [note, setNote] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [trackTitle, setTrackTitle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [liveCharCount, setLiveCharCount] = useState(0);
  const { audioElements, selectedPlayer, isPlaying, playAll, pauseAll, forward, rewind, setLastStartPositions } = useAudioContext();
  const { getNote, addNote, getTotalCharacterCount, getRemainingCharacters, canAddCharacters, trackNotes } = useNotes();
  const { getCustomDisplayName, setDisplayName: saveDisplayName, hasCustomDisplayName } = useDisplayNames();
  const toast = useToast();
  const editorRef = useRef<HTMLDivElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const positionMarkerRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const markerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const hasLongPressed = useRef(false);
  const shouldAutoInsertRef = useRef(false);
  const shouldAutoInsertChordRef = useRef(false);
  const isTextStampFromHotkeyRef = useRef(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Refs for optimized timestamp highlighting
  const sortedTimestampsRef = useRef<{ element: HTMLElement; time: number; type: 'chord' | 'timestamp' }[]>([]);
  const currentHighlightedIndexRef = useRef<number>(-1);

  // Rebuild timestamp cache when note content changes
  const rebuildTimestampCache = () => {
    if (!editorRef.current) return;
    
    const chordElements = editorRef.current.querySelectorAll('.chord');
    const timestampElements = editorRef.current.querySelectorAll('.timestamp');
    
    const timestamps: { element: HTMLElement; time: number; type: 'chord' | 'timestamp' }[] = [];
    
    chordElements.forEach((chordElement) => {
      const timestamp = chordElement.getAttribute('data-timestamp');
      if (timestamp) {
        const chordTime = parseFloat(timestamp);
        timestamps.push({ element: chordElement as HTMLElement, time: chordTime, type: 'chord' });
      }
    });
    
    timestampElements.forEach((timestampElement) => {
      const timestamp = timestampElement.getAttribute('data-timestamp');
      if (timestamp) {
        const time = parseFloat(timestamp);
        timestamps.push({ element: timestampElement as HTMLElement, time: time, type: 'timestamp' });
      }
    });
    
    // Sort by timestamp
    timestamps.sort((a, b) => a.time - b.time);
    sortedTimestampsRef.current = timestamps;
    currentHighlightedIndexRef.current = -1; // Reset highlighted index when cache is rebuilt
  };

  const updatePositionMarker = () => {
    const audio = audioElements.get(trackNumber);
    if (audio && positionMarkerRef.current && audio.duration > 0) {
      const position = (audio.currentTime / audio.duration) * 100;
      positionMarkerRef.current.style.left = `${position}%`;
    }
    // Note: Highlighting is now handled by a dedicated useEffect with interval checking
  };

  // Legacy function kept for compatibility but highlighting is now handled by useEffect
  const updateChordHighlighting = (currentTime: number) => {
    // This function is no longer used for highlighting during playback
    // Highlighting is now handled by the dedicated useEffect with interval checking
  };

  const clearChordHighlighting = () => {
    // Use cached timestamps for efficient clearing
    sortedTimestampsRef.current.forEach(({ element, type }) => {
      element.style.backgroundColor = 'transparent';
      if (type === 'chord') {
        element.style.color = '#FF8C00'; // Original orange for chords
      } else {
        element.style.color = '#4299E1'; // Original blue for timestamps
      }
      element.style.borderRadius = '0';
      element.style.padding = '0';
    });
  };

  // Edit a text stamp (timestamp or chord) inline
  const editTextStamp = (element: HTMLElement) => {
    const isChord = element.classList.contains('chord');
    // For chords, get the text directly (no brackets to remove)
    // For timestamps, remove brackets if they exist
    const currentText = isChord ? (element.textContent || '') : (element.textContent?.replace(/[[\]]/g, '') || '');
    
    // Create an input element
    const input = document.createElement('input');
    input.type = 'text';
    input.value = currentText;
    input.style.cssText = `
      background: #4a5568;
      color: #f7fafc;
      border: 1px solid #718096;
      border-radius: 6px;
      padding: 8px 12px;
      font-size: inherit;
      font-family: inherit;
      min-width: 80px;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
    `;
    
    // Add focus styling
    input.addEventListener('focus', () => {
      input.style.borderColor = '#3182ce';
      input.style.boxShadow = '0 0 0 1px #3182ce';
    });
    
    input.addEventListener('blur', () => {
      input.style.borderColor = '#718096';
      input.style.boxShadow = 'none';
    });
    
    // Replace the element with the input
    const parent = element.parentNode;
    if (parent) {
      parent.replaceChild(input, element);
      input.focus();
      input.select();
      
      const finishEdit = () => {
        const newText = input.value.trim();
        if (newText && newText !== currentText) {
          // Update the text content of the existing element
          element.textContent = newText;
          // Restore the element to the DOM
          parent.replaceChild(element, input);
          
          // Trigger input event to update state
          const event = new Event('input', { bubbles: true });
          if (editorRef.current) {
            editorRef.current.dispatchEvent(event);
          }
          
          // Re-attach event handlers for the edited element
          attachEventHandlers();
        } else {
          // Restore original element if no change or empty
          parent.replaceChild(element, input);
        }
      };
      
      // Handle Enter key and blur
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finishEdit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          parent.replaceChild(element, input);
        }
      });
      
      input.addEventListener('blur', finishEdit);
    }
  };


  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayName(newValue);
    
    // Save the display name (empty string will clear it and revert to original)
    if (fileId) {
      saveDisplayName(fileId, newValue);
    }
  };

  // Calculate live character count including current editor content
  const calculateLiveCharCount = (currentEditorContent: string) => {
    // Get total count of all saved notes except current file
    const totalWithoutCurrent = Object.entries(trackNotes)
      .filter(([key]) => fileId && key !== fileId)
      .reduce((total, [, noteContent]) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = noteContent;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';
        return total + plainText.length;
      }, 0);

    // Add current editor content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currentEditorContent;
    const currentPlainText = tempDiv.textContent || tempDiv.innerText || '';
    
    return totalWithoutCurrent + currentPlainText.length;
  };

  useEffect(() => {
    if (isOpen && fileId) {
      console.log('Modal opening for track', trackNumber, 'fileId', fileId);
      const savedNote = getNote(fileId);
      console.log('Retrieved saved note:', savedNote);
      
      // Get track title from audio element
      const audio = audioElements.get(trackNumber);
      const title = audio?.title || `Track ${trackNumber}`;
      setTrackTitle(title);
      
      // Get custom display name or fallback to actual filename
      const customName = fileId ? getCustomDisplayName(fileId) : '';
      setDisplayName(customName || title);
      
      setNote(savedNote);
      // Initialize live character count
      setLiveCharCount(calculateLiveCharCount(savedNote));
      
      // Store the auto-insert flags for later use (after waveform is ready)
      shouldAutoInsertRef.current = !!window.__autoInsertTimestamp;
      shouldAutoInsertChordRef.current = !!window.__autoInsertChord;
      if (shouldAutoInsertRef.current) {
        console.log('Auto-insert timestamp flag detected, will insert after waveform is ready');
        window.__autoInsertTimestamp = false; // Clear the flag
      }
      if (shouldAutoInsertChordRef.current) {
        console.log('Auto-insert chord flag detected, will insert after waveform is ready');
        window.__autoInsertChord = false; // Clear the flag
      }
      
      let retryCount = 0;
      const maxRetries = 5;
      const retryDelay = 100;
      let retryTimeout: NodeJS.Timeout | null = null;

      const tryInitWaveform = () => {
        if (editorRef.current) {
          console.log('Setting editor content to:', savedNote);
          editorRef.current.innerHTML = savedNote;
          // Don't auto-focus on mobile to prevent keyboard popup
          if (!isMobileDevice()) {
            editorRef.current.focus();
          }

          // Auto-insert timestamp if requested via ⌘I from main player
          if (shouldAutoInsertRef.current) {
            console.log('Editor ready, auto-inserting timestamp immediately');
            shouldAutoInsertRef.current = false; // Clear the flag
            // Small delay to ensure editor is fully rendered
            setTimeout(() => {
              insertTimestampAtEnd();
            }, 10);
          }
          
          // Auto-insert chord if requested via ⌃⌘C from main player
          if (shouldAutoInsertChordRef.current) {
            console.log('Editor ready, auto-inserting chord immediately');
            shouldAutoInsertChordRef.current = false; // Clear the flag
            // Small delay to ensure editor is fully rendered, then open chord input
            setTimeout(() => {
              insertChordAtEnd();
            }, 10);
          }

          // Place cursor on a new line after existing content
          if (savedNote && editorRef.current.childNodes.length > 0) {
            // Check if last node is a <br> (new line), if not, add one
            let lastNode = editorRef.current.lastChild;
            if (!lastNode || lastNode.nodeName !== 'BR') {
              const br = document.createElement('br');
              editorRef.current.appendChild(br);
              lastNode = br;
            }
            // Place cursor after the <br>
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStartAfter(lastNode);
            range.collapse(true);
            selection?.removeAllRanges();
            selection?.addRange(range);
          }

          // Attach event handlers for timestamps and chords
          attachEventHandlers();
        }

        if (waveformRef.current) {
          const audio = audioElements.get(trackNumber);
          if (audio && audio.src) {
            if (wavesurferRef.current) {
              wavesurferRef.current.destroy();
              wavesurferRef.current = null;
            }
            try {
              const backend = isMobileDevice() ? 'MediaElement' : 'WebAudio';
              console.log(`Initializing WaveSurfer with ${backend} backend for notes modal track ${trackNumber}`);
              const wavesurfer = WaveSurfer.create({
                container: waveformRef.current,
                waveColor: 'rgba(66, 153, 225, 0.3)',
                progressColor: 'rgba(66, 153, 225, 0.8)',
                cursorColor: 'transparent',
                barWidth: 2,
                barRadius: 2,
                cursorWidth: 1,
                height: 40,
                barGap: 1,
                normalize: true,
                backend,
                hideScrollbar: true,
                interact: true,
                fillParent: true,
              });
              wavesurferRef.current = wavesurfer;
              wavesurfer.load(audio.src);
              wavesurfer.on('ready', () => {
                console.log(`Waveform ready for track ${trackNumber} with ${backend} backend`);
                setDuration(wavesurfer.getDuration());
                

              });
              wavesurfer.on('interaction', () => {
                const currentTime = wavesurfer.getCurrentTime();
                audioElements.forEach(audio => {
                  audio.currentTime = currentTime;
                });
                setCurrentTime(currentTime);
                updatePositionMarker();
              });
              wavesurfer.on('destroy', () => {
                console.log(`Waveform destroyed for track ${trackNumber}`);
              });
              // Set up sync interval for wavesurfer seeking
              syncIntervalRef.current = setInterval(() => {
                if (audio && wavesurfer.getDuration()) {
                  const currentTime = audio.currentTime;
                  setCurrentTime(currentTime);
                  wavesurfer.seekTo(currentTime / audio.duration);
                  updatePositionMarker();
                }
              }, 50);
              
              // Set up event-driven marker updates using timeupdate event
              const handleTimeUpdate = () => {
                updatePositionMarker();
              };
              audio.addEventListener('timeupdate', handleTimeUpdate);
              // Clean up on modal close
              return () => {
                if (syncIntervalRef.current) {
                  clearInterval(syncIntervalRef.current);
                }
                audio.removeEventListener('timeupdate', handleTimeUpdate);
                if (longPressTimer.current) {
                  clearTimeout(longPressTimer.current);
                  longPressTimer.current = null;
                }
                if (wavesurferRef.current) {
                  wavesurferRef.current.destroy();
                  wavesurferRef.current = null;
                }
              };
            } catch (error) {
              console.error(`Failed to initialize WaveSurfer for notes modal track ${trackNumber}:`, error);
            }
          } else {
            // Audio not ready, retry
            if (retryCount < maxRetries) {
              retryCount++;
              retryTimeout = setTimeout(tryInitWaveform, retryDelay);
            } else {
              console.warn('Audio element not ready for track', trackNumber, 'after retries');
            }
          }
        } else {
          // Waveform container not ready, retry
          if (retryCount < maxRetries) {
            retryCount++;
            retryTimeout = setTimeout(tryInitWaveform, retryDelay);
          } else {
            console.warn('Waveform container not ready for track', trackNumber, 'after retries');
          }
        }
      };

      tryInitWaveform();

      // Cleanup retry timeout on unmount/close
      return () => {
        if (retryTimeout) clearTimeout(retryTimeout);
        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
      };
    }
  }, [isOpen, trackNumber, audioElements, getNote]);

  // Keep the focus management effect
  useEffect(() => {
    if (isOpen && editorRef.current) {
      // Focus the editor when modal opens (but not on mobile to prevent keyboard popup)
      if (!isMobileDevice()) {
        editorRef.current.focus();
      }
      
      // Add click handler to maintain focus (but not on mobile)
      const handleClick = (e: MouseEvent) => {
        if (e.target instanceof HTMLElement && !e.target.closest('.editor')) {
          // Don't prevent default for buttons or interactive elements
          const isButton = e.target.closest('button') || e.target.closest('[role="button"]') || e.target.closest('input') || e.target.closest('textarea');
          if (!isButton) {
            e.preventDefault();
          }
          if (!isMobileDevice()) {
            editorRef.current?.focus();
          }
        }
      };

      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [isOpen]);

  // Focus modal content on mobile to prevent input auto-focus
  useEffect(() => {
    if (isOpen && isMobileDevice()) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        // Focus the modal content instead of any input field
        if (modalContentRef.current) {
          modalContentRef.current.focus();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Rebuild timestamp cache when note is loaded (not on every keystroke)
  // Cache is rebuilt when timestamps are actually added/edited, not when typing regular text
  useEffect(() => {
    if (isOpen && editorRef.current && note) {
      // Rebuild when note is first loaded (in case it has timestamps)
      rebuildTimestampCache();
    }
  }, [note, isOpen]); // Only when note changes or modal opens, not on every editContent change

  // Update highlighting during playback - optimized to only update on boundary crossings
  useEffect(() => {
    if (!isPlaying) {
      clearChordHighlighting();
      currentHighlightedIndexRef.current = -1;
      return;
    }

    const audio = audioElements.get(trackNumber);
    if (!audio || !editorRef.current) {
      return;
    }

    // Rebuild cache if empty
    if (sortedTimestampsRef.current.length === 0) {
      rebuildTimestampCache();
    }

    // Find which element should be highlighted
    const findHighlightedIndex = (time: number): number => {
      for (let i = 0; i < sortedTimestampsRef.current.length; i++) {
        const elementTime = sortedTimestampsRef.current[i].time;
        const nextElementTime = i < sortedTimestampsRef.current.length - 1 
          ? sortedTimestampsRef.current[i + 1].time 
          : Infinity;
        
        if (time >= elementTime && time < nextElementTime) {
          return i;
        }
      }
      return -1;
    };

    // Update highlighting only when crossing a boundary
    const updateHighlighting = () => {
      const currentTime = audio.currentTime;
      const newHighlightedIndex = findHighlightedIndex(currentTime);
      
      // Only update if the highlighted index has changed
      if (newHighlightedIndex !== currentHighlightedIndexRef.current) {
        // Clear previous highlight
        if (currentHighlightedIndexRef.current >= 0 && 
            currentHighlightedIndexRef.current < sortedTimestampsRef.current.length) {
          const prevItem = sortedTimestampsRef.current[currentHighlightedIndexRef.current];
          prevItem.element.style.backgroundColor = 'transparent';
          if (prevItem.type === 'chord') {
            prevItem.element.style.color = '#FF8C00';
          } else {
            prevItem.element.style.color = '#4299E1';
          }
          prevItem.element.style.borderRadius = '0';
          prevItem.element.style.padding = '0';
        }
        
        // Apply new highlight
        if (newHighlightedIndex >= 0) {
          const newItem = sortedTimestampsRef.current[newHighlightedIndex];
          newItem.element.style.backgroundColor = '#FFD700';
          newItem.element.style.color = '#000000';
          newItem.element.style.borderRadius = '3px';
          newItem.element.style.padding = '1px 3px';
          
          // Scroll the highlighted element into view
          newItem.element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
        
        currentHighlightedIndexRef.current = newHighlightedIndex;
      }
    };

    // Update immediately
    updateHighlighting();

    // Use a small interval to check frequently (every 100ms) but only update DOM on boundary crossings
    const checkInterval = setInterval(() => {
      if (!audio.paused) {
        updateHighlighting();
      }
    }, 100);

    // Also listen to timeupdate as a backup
    if (audio.addEventListener) {
      audio.addEventListener('timeupdate', updateHighlighting);
    }

    return () => {
      clearInterval(checkInterval);
      if (audio.removeEventListener) {
        audio.removeEventListener('timeupdate', updateHighlighting);
      }
    };
  }, [isPlaying, trackNumber, audioElements, note, isOpen]);

  const handleSave = () => {
    if (editorRef.current) {
      let content = editorRef.current.innerHTML;
      
      console.log('Original content before trimming:', JSON.stringify(content));
      
      // Trim whitespace from the content
      // Remove trailing empty divs, <br> elements, &nbsp; and other whitespace
      content = content.replace(/(<div\s*><br\s*\/?><\/div>|<br\s*\/?>|&nbsp;|\s)+$/gi, '');
      
      // Also trim leading whitespace 
      content = content.replace(/^(<div\s*><br\s*\/?><\/div>|<br\s*\/?>|&nbsp;|\s)+/gi, '');
      
      console.log('Content after trimming:', JSON.stringify(content));
      console.log('Saving note for track', trackNumber, 'fileId', fileId, ':', content);
      if (fileId) {
        addNote(fileId, content);
      }
      setNote(content);
      toast({
        title: 'Note saved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onClose();
    }
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      pauseAll();
    } else {
      playAll();
    }
  };

  const handleSeek = (seconds: number) => {
    if (seconds > 0) {
      forward(seconds);
    } else {
      rewind(-seconds);
    }
    // Update marker position immediately after seeking
    updatePositionMarker();
  };

  // Helper to check if any track is over 10 minutes
  const hasLongTracks = (audioElements: Map<number, HTMLAudioElement>): boolean => {
    for (const audio of audioElements.values()) {
      if (audio.duration && audio.duration > 600) { // 600 seconds = 10 minutes
        return true;
      }
    }
    return false;
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const useLeadingZero = hasLongTracks(audioElements);
    const minutesStr = useLeadingZero 
      ? minutes.toString().padStart(2, '0')
      : minutes.toString();
    return `${minutesStr}:${seconds.toString().padStart(2, '0')}`;
  };

  // Helper function to check if editor has meaningful content (not just empty elements or whitespace)
  const hasMeaningfulContent = (element: HTMLElement): boolean => {
    if (!element) {
      console.log('hasMeaningfulContent: no element');
      return false;
    }
    
    // Get innerHTML and strip it to check for actual content
    const html = element.innerHTML.trim();
    console.log('hasMeaningfulContent: original html:', JSON.stringify(html));
    if (!html) {
      console.log('hasMeaningfulContent: html is empty, returning false');
      return false;
    }
    
    // Remove all <br> tags and empty divs to check for actual content
    // This handles cases where browser leaves behind empty elements after deletion
    const cleanedHtml = html.replace(/<br\s*\/?>/gi, '').replace(/<div\s*><\/div>/gi, '').replace(/<div\s*><br\s*\/?><\/div>/gi, '');
    console.log('hasMeaningfulContent: cleaned html:', JSON.stringify(cleanedHtml));
    if (!cleanedHtml.trim()) {
      console.log('hasMeaningfulContent: cleaned html is empty, returning false');
      return false;
    }
    
    // Create a temporary element to strip HTML and get text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanedHtml;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    console.log('hasMeaningfulContent: textContent:', JSON.stringify(textContent));
    
    // Check if there's any meaningful text after trimming whitespace
    const result = textContent.trim().length > 0;
    console.log('hasMeaningfulContent: final result:', result);
    return result;
  };

  const insertTimestamp = (customText?: string) => {
    const audio = audioElements.get(trackNumber);
    if (audio && editorRef.current) {
      const preciseTime = audio.currentTime;
      const timestamp = formatTime(preciseTime);
      
      // Create the timestamp element
      const timestampElement = document.createElement('span');
      timestampElement.className = customText ? 'chord' : 'timestamp';
      timestampElement.textContent = customText || `[${timestamp}]`;
      timestampElement.style.color = customText ? '#FF8C00' : '#4299E1';
      timestampElement.style.cursor = 'pointer';
      timestampElement.style.textDecoration = customText ? 'none' : 'underline';
      timestampElement.contentEditable = 'false'; // Make timestamp non-editable
      timestampElement.style.userSelect = 'none'; // Prevent text selection when clicked
      timestampElement.onclick = (e) => {
        e.preventDefault();
        // Use the precise time stored in data-timestamp
        const preciseTime = parseFloat(timestampElement.getAttribute('data-timestamp') || '0');
        
        // Set time for all audio elements
        audioElements.forEach(audio => {
          audio.currentTime = preciseTime;
        });
        
        setCurrentTime(preciseTime);
      };
      
      // Store the precise timestamp data for click handling (both chords and timestamps)
      timestampElement.setAttribute('data-timestamp', preciseTime.toString());

      // Ensure editor is focused (but not on mobile)
      if (!isMobileDevice()) {
        editorRef.current.focus();
      }

      // Get current selection
      const selection = window.getSelection();
      let range = null;
      try {
        if (selection && selection.rangeCount > 0) {
          range = selection.getRangeAt(0);
        }
      } catch (error) {
        console.log('Error getting selection range:', error);
        // Continue without range - we'll append to the end
      }

      // --- Ensure exactly one space before timestamp ---
      if (range && range.collapsed) {
        try {
          // If not at the start of the editor
          if (range.startOffset > 0) {
            const container = range.startContainer;
            const offset = range.startOffset;
            if (container.nodeType === Node.TEXT_NODE) {
              let text = container.textContent || '';
              // Remove all spaces to the left of the cursor
              const left = text.slice(0, offset).replace(/\s+$/, '');
              const right = text.slice(offset);
              // Always add one space
              text = left + ' ' + right;
              // Update the text node
              container.textContent = text;
              // Move cursor after the single space
              range.setStart(container, left.length + 1);
              range.collapse(true);
            } else if (container.nodeType === Node.ELEMENT_NODE && offset > 0) {
              // Check previous sibling if it's a text node
              const prevNode = container.childNodes[offset - 1];
              if (prevNode && prevNode.nodeType === Node.TEXT_NODE) {
                let text = prevNode.textContent || '';
                // Remove all spaces at the end
                text = text.replace(/\s+$/, '');
                prevNode.textContent = text + ' ';
                // Move cursor after the single space
                range.setStart(prevNode, prevNode.textContent.length);
                range.collapse(true);
              } else if (prevNode && prevNode.nodeType === Node.ELEMENT_NODE && prevNode.nodeName !== 'BR') {
                // If previous node is not a <br>, ensure a single space node
                const spaceNode = document.createTextNode(' ');
                container.insertBefore(spaceNode, container.childNodes[offset]);
                range.setStartAfter(spaceNode);
                range.collapse(true);
              }
            }
          }
        } catch (error) {
          console.log('Error manipulating range for spacing, continuing without range:', error);
          range = null; // Force fallback to append mode
        }
      }

      if (range) {
        try {
          // Insert timestamp
          range.insertNode(timestampElement);
          // Insert space after timestamp
          const space = document.createTextNode('\u00A0'); // Non-breaking space
          range.setStartAfter(timestampElement);
          range.insertNode(space);
          // Move cursor after space
          range.setStartAfter(space);
          range.collapse(true);
          // Update selection
          selection?.removeAllRanges();
          selection?.addRange(range);
        } catch (error) {
          console.log('Error manipulating range, falling back to append:', error);
          // Fall back to appending at the end
          editorRef.current.appendChild(timestampElement);
          editorRef.current.appendChild(document.createTextNode('\u00A0'));
        }
      } else {
        // If no selection, append to end
        // Insert space if needed
        if (editorRef.current.lastChild && editorRef.current.lastChild.nodeType === Node.TEXT_NODE) {
          const text = editorRef.current.lastChild.textContent || '';
          if (text[text.length - 1] && text[text.length - 1] !== ' ') {
            editorRef.current.appendChild(document.createTextNode(' '));
          }
        }
        editorRef.current.appendChild(timestampElement);
        editorRef.current.appendChild(document.createTextNode('\u00A0'));
        // Move cursor to end
        const newRange = document.createRange();
        newRange.selectNodeContents(editorRef.current);
        newRange.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(newRange);
      }
      // Rebuild timestamp cache after inserting
      rebuildTimestampCache();
      // Trigger input event to update state
      const event = new Event('input', { bubbles: true });
      editorRef.current.dispatchEvent(event);
    }
  };

  const insertTimestampAtEnd = () => {
    console.log('insertTimestampAtEnd called');
    if (editorRef.current) {
      console.log('Editor ref available, positioning cursor at end and inserting timestamp');
      console.log('Editor innerHTML before insert:', JSON.stringify(editorRef.current.innerHTML));
      
      // Check if the editor is currently focused
      const isEditorFocused = document.activeElement === editorRef.current;
      console.log('Editor focused:', isEditorFocused);
      
      // Check for meaningful content
      const hasContent = hasMeaningfulContent(editorRef.current);
      console.log('hasMeaningfulContent returned:', hasContent);
      
      // If there's no meaningful content, clean up any empty elements (like <br> tags left by browser)
      if (!hasContent) {
        console.log('No meaningful content, cleaning up empty elements');
        // Remove all <br> tags and empty divs
        const brElements = editorRef.current.querySelectorAll('br');
        brElements.forEach(br => br.remove());
        const emptyDivs = Array.from(editorRef.current.querySelectorAll('div')).filter(div => {
          const text = div.textContent?.trim() || '';
          return text === '' && div.children.length === 0;
        });
        emptyDivs.forEach(div => div.remove());
        // Also check if the editor itself only contains empty elements
        if (editorRef.current.childNodes.length === 0 || 
            (editorRef.current.childNodes.length === 1 && editorRef.current.firstChild?.nodeName === 'BR')) {
          editorRef.current.innerHTML = '';
        }
      }
      
      // Ensure cursor is positioned at the end of the content
      const range = document.createRange();
      const selection = window.getSelection();
      
      // Move cursor to the very end of the editor content
      range.selectNodeContents(editorRef.current);
      range.collapse(false); // false = collapse to end
      selection?.removeAllRanges();
      selection?.addRange(range);
      
      // Add a line break if editor is NOT focused AND there's existing meaningful content
      // But only if there isn't already a <br> at the end
      if (!isEditorFocused && hasContent) {
        const lastChild = editorRef.current.lastChild;
        console.log('Last child before check:', lastChild?.nodeName, lastChild);
        // Check if the last child is not already a <br> element
        if (!lastChild || lastChild.nodeName !== 'BR') {
          console.log('Adding <br> before timestamp');
          editorRef.current.appendChild(document.createElement('br'));
          // Update the range to be after the new line break
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        } else {
          console.log('Last child is already <br>, not adding another');
        }
      } else {
        console.log('Not adding <br> - isEditorFocused:', isEditorFocused, 'hasContent:', hasContent);
      }

      // Call the existing insertTimestamp function which handles everything properly
      insertTimestamp();
    }
  };

  // Attach event handlers for timestamps and chords
  const attachEventHandlers = () => {
    if (!editorRef.current) return;
    
    // Remove existing handlers first to avoid duplicates
    editorRef.current.removeEventListener('click', handleTimestampClick);
    editorRef.current.removeEventListener('dblclick', handleTimestampDoubleClick);
    editorRef.current.removeEventListener('touchend', handleTimestampTouch);
    
    // Add click handler for timestamps and chords
    editorRef.current.addEventListener('click', handleTimestampClick);
    editorRef.current.addEventListener('dblclick', handleTimestampDoubleClick);
    editorRef.current.addEventListener('touchend', handleTimestampTouch);
  };

  // Click handler for timestamps and chords
  const handleTimestampClick = (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('timestamp') || target.classList.contains('chord')) {
      e.preventDefault();
      // Always use the precise time stored in data-timestamp for both timestamps and chords
      const preciseTime = parseFloat(target.getAttribute('data-timestamp') || '0');
      
      if (preciseTime >= 0) {
        // Set time for all audio elements
        audioElements.forEach(audio => {
          audio.currentTime = preciseTime;
        });
        
        setCurrentTime(preciseTime);
        
        // Update last start positions if playing
        if (isPlaying) {
          const newStartPositions = new Map<number, number>();
          audioElements.forEach((audio, playerNumber) => {
            newStartPositions.set(playerNumber, audio.currentTime);
          });
          setLastStartPositions(newStartPositions);
        }
      }
    }
  };

  // Double-click handler for editing timestamps and chords
  const handleTimestampDoubleClick = (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('timestamp') || target.classList.contains('chord')) {
      e.preventDefault();
      e.stopPropagation();
      editTextStamp(target);
    }
  };

  // Touch handler for mobile devices
  const handleTimestampTouch = (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('timestamp') || target.classList.contains('chord')) {
      e.preventDefault();
      const timestamp = target.textContent?.replace(/[[\]]/g, '');
      if (timestamp) {
        // For chords, we need to get the timestamp from the data attribute
        let timeInSeconds;
        if (target.classList.contains('chord')) {
          timeInSeconds = parseFloat(target.getAttribute('data-timestamp') || '0');
        } else {
          const [minutes, seconds] = timestamp.split(':').map(Number);
          timeInSeconds = minutes * 60 + seconds;
        }
        
        // Set time for all audio elements
        audioElements.forEach(audio => {
          audio.currentTime = timeInSeconds;
        });
        
        setCurrentTime(timeInSeconds);
        
        // Update last start positions if playing
        if (isPlaying) {
          const newStartPositions = new Map<number, number>();
          audioElements.forEach((audio, playerNumber) => {
            newStartPositions.set(playerNumber, audio.currentTime);
          });
          setLastStartPositions(newStartPositions);
        }
      }
    }
  };

  // Create inline text stamp input (similar to editTextStamp but for new stamps)
  const createInlineTextStampInput = () => {
    if (!editorRef.current) return;
    
    // Check if the editor is currently focused
    const isEditorFocused = document.activeElement === editorRef.current;
    
    // Capture current timestamp
    const audio = audioElements.get(trackNumber);
    const preciseTime = audio ? audio.currentTime : 0;
    const timestamp = formatTime(preciseTime);
    
    // Create an input element
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Enter text stamp...';
    input.style.cssText = `
      background: #4a5568;
      color: #f7fafc;
      border: 1px solid #718096;
      border-radius: 6px;
      padding: 8px 12px;
      font-size: inherit;
      font-family: inherit;
      min-width: 120px;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
    `;
    
    // Add focus styling
    input.addEventListener('focus', () => {
      input.style.borderColor = '#3182ce';
      input.style.boxShadow = '0 0 0 1px #3182ce';
    });
    
    input.addEventListener('blur', () => {
      input.style.borderColor = '#718096';
      input.style.boxShadow = 'none';
    });
    
    // Check for meaningful content first (before any insertion logic)
    const hasContent = hasMeaningfulContent(editorRef.current);
    console.log('createInlineTextStampInput - hasContent:', hasContent, 'isEditorFocused:', isEditorFocused);
    console.log('createInlineTextStampInput - editor innerHTML before cleanup:', JSON.stringify(editorRef.current.innerHTML));
    
    // If there's no meaningful content, clean up any empty elements (like <br> tags left by browser)
    // This should happen regardless of focus state to ensure clean insertion
    if (!hasContent) {
      console.log('No meaningful content, cleaning up empty elements');
      // Remove all <br> tags and empty divs
      const brElements = editorRef.current.querySelectorAll('br');
      console.log('Found', brElements.length, 'br elements to remove');
      brElements.forEach(br => br.remove());
      const emptyDivs = Array.from(editorRef.current.querySelectorAll('div')).filter(div => {
        const text = div.textContent?.trim() || '';
        return text === '' && div.children.length === 0;
      });
      console.log('Found', emptyDivs.length, 'empty divs to remove');
      emptyDivs.forEach(div => div.remove());
      // Also check if the editor itself only contains empty elements
      if (editorRef.current.childNodes.length === 0 || 
          (editorRef.current.childNodes.length === 1 && editorRef.current.firstChild?.nodeName === 'BR')) {
        console.log('Clearing editor innerHTML completely');
        editorRef.current.innerHTML = '';
      }
      console.log('Editor innerHTML after cleanup:', JSON.stringify(editorRef.current.innerHTML));
    }
    
    // If editor is not focused, ensure cursor is at end and add a new line if there's content
    if (!isEditorFocused) {
      const range = document.createRange();
      const selection = window.getSelection();
      
      // Move cursor to the very end of the editor content
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
      
      // Add a line break if there's existing meaningful content
      // But only if there isn't already a <br> at the end
      if (hasContent) {
        const lastChild = editorRef.current.lastChild;
        // Check if the last child is not already a <br> element
        if (!lastChild || lastChild.nodeName !== 'BR') {
          editorRef.current.appendChild(document.createElement('br'));
          
          // Update the range to be after the new line break
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }
    }
    
    // Insert the input at the current cursor position or at the end
    const selection = window.getSelection();
    let shouldUseSelection = false;
    
    if (selection && selection.rangeCount > 0 && editorRef.current) {
      const range = selection.getRangeAt(0);
      const commonAncestor = range.commonAncestorContainer;
      
      // Check if the selection is actually within the editor
      // The common ancestor should be the editor itself or a descendant of it
      if (editorRef.current.contains(commonAncestor) || editorRef.current === commonAncestor) {
        shouldUseSelection = true;
        
        // Always add a space before the input if there's any content before the cursor
        const editorContent = editorRef.current?.textContent || '';
        const cursorPosition = range.startOffset;
        
        console.log('Creating text stamp at cursor position:', {
          editorContent: editorContent.substring(0, 50) + '...',
          cursorPosition,
          beforeCursor: editorContent.substring(0, cursorPosition),
          needsSpace: editorContent.length > 0 && !editorContent.substring(0, cursorPosition).endsWith(' ')
        });
        
        // No need to add spaces - let the browser handle natural text flow
        
        range.insertNode(input);
      }
    }
    
    if (!shouldUseSelection) {
      // Fallback: append to end
      const editorContent = editorRef.current?.textContent || '';
      console.log('Creating text stamp at end:', {
        editorContent: editorContent.substring(0, 50) + '...',
        needsSpace: editorContent.length > 0 && !editorContent.endsWith(' ')
      });
      
      // Safety check: if the last child is a <br> and there's no meaningful content, remove it before appending
      if (!hasContent && editorRef.current?.lastChild?.nodeName === 'BR') {
        console.log('Removing trailing <br> before appending text stamp input');
        editorRef.current.lastChild.remove();
      }
      
      // No need to add spaces - let the browser handle natural text flow
      editorRef.current?.appendChild(input);
      console.log('Editor innerHTML after appending input:', JSON.stringify(editorRef.current?.innerHTML));
    }
    
    input.focus();
    input.select();
    
    let isFinishing = false;
    const finishTextStamp = () => {
      if (isFinishing) return;
      isFinishing = true;
      
      const textContent = input.value.trim();
      if (textContent) {
        // Create the chord element
        const chordElement = document.createElement('span');
        chordElement.className = 'chord';
        chordElement.textContent = textContent;
        chordElement.style.color = '#FF8C00';
        chordElement.style.cursor = 'pointer';
        chordElement.style.textDecoration = 'none';
        chordElement.contentEditable = 'false';
        chordElement.style.userSelect = 'none';
        chordElement.onclick = (e) => {
          e.preventDefault();
          // Use the precise time stored in data-timestamp
          const preciseTime = parseFloat(chordElement.getAttribute('data-timestamp') || '0');
          
          // Set time for all audio elements
          audioElements.forEach(audio => {
            audio.currentTime = preciseTime;
          });
        };
        
        // Store the precise timestamp data for click handling
        chordElement.setAttribute('data-timestamp', preciseTime.toString());
        
        // Replace input with the chord element
        const parent = input.parentNode;
        if (parent && parent.contains(input)) {
          try {
            parent.replaceChild(chordElement, input);
            // Move cursor after the chord element
            const newRange = document.createRange();
            newRange.setStartAfter(chordElement);
            newRange.collapse(true);
            selection?.removeAllRanges();
            selection?.addRange(newRange);
            
            // Trigger input event to update state
            const event = new Event('input', { bubbles: true });
            editorRef.current?.dispatchEvent(event);
            
            // Re-attach event handlers for the new chord element
            attachEventHandlers();
          } catch (error) {
            console.warn('Error replacing input with chord element:', error);
            // Fallback: just append the chord element
            parent.appendChild(chordElement);
            
            // Move cursor after the chord element
            const newRange = document.createRange();
            newRange.setStartAfter(chordElement);
            newRange.collapse(true);
            selection?.removeAllRanges();
            selection?.addRange(newRange);
            
            // Trigger input event to update state
            const event = new Event('input', { bubbles: true });
            editorRef.current?.dispatchEvent(event);
            
            // Re-attach event handlers for the new chord element
            attachEventHandlers();
          }
        } else {
          // Fallback: just append the chord element
          editorRef.current?.appendChild(chordElement);
          
          // Move cursor after the chord element
          const newRange = document.createRange();
          newRange.setStartAfter(chordElement);
          newRange.collapse(true);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(newRange);
          
          // Trigger input event to update state
          const event = new Event('input', { bubbles: true });
          editorRef.current?.dispatchEvent(event);
          
          // Re-attach event handlers for the new chord element
          attachEventHandlers();
        }
        
        // If this textstamp was added via hotkey, return focus to main page
        if (isTextStampFromHotkeyRef.current) {
          isTextStampFromHotkeyRef.current = false;
          // Blur the editor to remove focus from the text area
          editorRef.current?.blur();
          // Focus on the modal content to allow hotkeys to work again
          modalContentRef.current?.focus();
        }
      } else {
        // Remove input if no text entered
        input.remove();
        
        // If this was from hotkey but no text entered, still return focus
        if (isTextStampFromHotkeyRef.current) {
          isTextStampFromHotkeyRef.current = false;
          editorRef.current?.blur();
          modalContentRef.current?.focus();
        }
      }
    };
    
    // Handle Enter key and blur
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        finishTextStamp();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        input.remove();
      }
    });
    
    input.addEventListener('blur', finishTextStamp);
  };

  const insertChordAtEnd = () => {
    console.log('insertChordAtEnd called');
    // Mark that this textstamp is from hotkey
    isTextStampFromHotkeyRef.current = true;
    if (editorRef.current) {
      console.log('Editor ref available, positioning cursor at end and creating inline text stamp input');
      
      // Check if the editor is currently focused
      const isEditorFocused = document.activeElement === editorRef.current;
      
      // Ensure cursor is positioned at the end of the content
      const range = document.createRange();
      const selection = window.getSelection();
      
      // Move cursor to the very end of the editor content
      range.selectNodeContents(editorRef.current);
      range.collapse(false); // false = collapse to end
      selection?.removeAllRanges();
      selection?.addRange(range);
      
      // Add a line break if editor is NOT focused AND there's existing meaningful content
      // But only if there isn't already a <br> at the end
      if (!isEditorFocused && hasMeaningfulContent(editorRef.current)) {
        const lastChild = editorRef.current.lastChild;
        // Check if the last child is not already a <br> element
        if (!lastChild || lastChild.nodeName !== 'BR') {
          editorRef.current.appendChild(document.createElement('br'));
          // Update the range to be after the new line break
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }

      createInlineTextStampInput();
    }
  };

  const insertChordInline = () => {
    console.log('insertChordInline called');
    // Mark that this textstamp is from hotkey
    isTextStampFromHotkeyRef.current = true;
    if (editorRef.current) {
      console.log('Editor ref available, creating inline text stamp input at current cursor position');
      createInlineTextStampInput();
    }
  };

  // Custom tab navigation between the three main focusable elements
  const handleTabNavigation = (shiftKey: boolean) => {
    const focusableElements = [
      nameInputRef.current,
      waveformRef.current,
      editorRef.current
    ].filter(Boolean); // Remove any null refs

    if (focusableElements.length === 0) return;

    const currentActiveElement = document.activeElement;
    let currentIndex = -1;

    // Find current focused element index
    for (let i = 0; i < focusableElements.length; i++) {
      if (focusableElements[i] === currentActiveElement) {
        currentIndex = i;
        break;
      }
    }

    // Calculate next index based on direction
    let nextIndex;
    if (shiftKey) {
      // Shift+Tab (reverse)
      nextIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
    } else {
      // Tab (forward)
      nextIndex = currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
    }

    // Focus the next element
    const nextElement = focusableElements[nextIndex];
    if (nextElement) {
      nextElement.focus();
    }
  };

  // Global keydown handler for modal shortcuts when editor is not focused
  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Handle Tab navigation for custom focus cycling
      if (e.key === 'Tab') {
        // Only handle tab if we're focused on one of our main elements
        const currentElement = document.activeElement;
        const isOnMainElement = currentElement === nameInputRef.current || 
                                currentElement === waveformRef.current || 
                                currentElement === editorRef.current;
        
        if (isOnMainElement) {
          e.preventDefault();
          e.stopPropagation();
          handleTabNavigation(e.shiftKey);
          return;
        }
      }

      // Handle ⌘↵ (Command + Enter) to save note from anywhere in the modal
      if (e.metaKey && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleSave();
        return;
      }

      // Handle ⌘I (Command + I) to insert timestamp when editor is not focused
      if (e.metaKey && !e.shiftKey && (e.key === 'i' || e.key === 'I')) {
        // Check if the editor is currently focused
        const isEditorFocused = document.activeElement === editorRef.current;
        const isNameInputFocused = document.activeElement === nameInputRef.current;
        
        // Only handle if editor is not focused and name input is not focused
        if (!isEditorFocused && !isNameInputFocused) {
          e.preventDefault();
          e.stopPropagation(); // Prevent bubbling to main page import hotkey
          insertTimestampAtEnd();
        }
      }

      // Handle ⇧⌘I (Shift + Command + I) to insert textstamp when editor is not focused
      if (e.metaKey && e.shiftKey && (e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
        e.stopPropagation();
        // Check if the editor is currently focused
        const isEditorFocused = document.activeElement === editorRef.current;
        const isNameInputFocused = document.activeElement === nameInputRef.current;
        
        // Only handle if editor is not focused and name input is not focused
        if (!isEditorFocused && !isNameInputFocused) {
          insertChordAtEnd();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isOpen]);

  const handleCancel = () => {
    onClose();
  };

  // Handle cursor positioning around text stamps
  const handleCursorPositioning = () => {
    const selection = window.getSelection();
    if (!selection || !editorRef.current) return;
    
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    
    // Check if cursor is inside a text stamp
    const textStamp = container.nodeType === Node.ELEMENT_NODE 
      ? container as Element
      : container.parentElement;
    
    if (textStamp && (textStamp.classList.contains('chord') || textStamp.classList.contains('timestamp'))) {
      // Move cursor to after the text stamp
      const newRange = document.createRange();
      newRange.setStartAfter(textStamp);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  };

  const handleFastRewindStart = () => {
    hasLongPressed.current = false;
    longPressTimer.current = setTimeout(() => {
      hasLongPressed.current = true;
      // Return to start for all audio elements
      audioElements.forEach(audio => {
        audio.currentTime = 0;
      });
      setCurrentTime(0);
      updatePositionMarker();
    }, 500);
  };

  const handleFastRewindEnd = (e: React.TouchEvent | React.MouseEvent) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      if (!hasLongPressed.current) {
        // Only trigger rewind if this is a touch event or if it's not a touch device
        if ('touches' in e || !isMobile) {
          handleSeek(-10);
        }
      }
    }
  };

  const handleFastRewindClick = (e: React.MouseEvent) => {
    // Only handle click events on non-mobile devices
    if (!isMobile && !hasLongPressed.current) {
      handleSeek(-10);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} closeOnEsc={false} size="xl">
      <ModalOverlay />
      <ModalContent ref={modalContentRef} tabIndex={-1}>
        <ModalHeader pt={6}>
          <Input
            ref={nameInputRef}
            value={displayName}
            onChange={handleDisplayNameChange}
            placeholder={trackTitle}
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor="whiteAlpha.200"
            color="#FED49A"
            fontSize="lg"
            fontWeight="600"
            autoFocus={false}
            _placeholder={{ color: "gray.500" }}
            _hover={{ borderColor: "whiteAlpha.300" }}
            _focus={{ 
              borderColor: "accent.500", 
              boxShadow: "0 0 0 1px var(--chakra-colors-accent-500)" 
            }}
            onKeyDown={(e) => {
              // Handle Tab navigation
              if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                handleTabNavigation(e.shiftKey);
                return;
              }
              
              // Handle ⌘↵ (Command + Enter) to save note
              if (e.metaKey && e.key === 'Enter') {
                e.preventDefault();
                handleSave();
                return;
              }
              
              // Handle ⌘I (Command + I) to insert timestamp
              if (e.metaKey && !e.shiftKey && (e.key === 'i' || e.key === 'I')) {
                e.preventDefault();
                insertTimestampAtEnd();
                return;
              }

              // Handle ⇧⌘I (Shift + Command + I) to insert textstamp
              if (e.metaKey && e.shiftKey && (e.key === 'i' || e.key === 'I')) {
                e.preventDefault();
                e.stopPropagation();
                insertChordInline();
                return;
              }
              
              // Handle Escape to cancel/close
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
                return;
              }
              
              // Prevent space and other hotkeys from triggering global handlers
              e.stopPropagation();
            }}
          />
        </ModalHeader>
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            
            <Box 
              ref={waveformRef}
              bg="gray.900"
              borderRadius="md"
              p={0.5}
              position="relative"
              minH="40px"
              w="100%"
              tabIndex={0}
              border="1px solid"
              borderColor="whiteAlpha.200"
              _focus={{ 
                borderColor: "accent.500", 
                boxShadow: "0 0 0 1px var(--chakra-colors-accent-500)",
                outline: "none"
              }}
              onKeyDown={(e) => {
                // Handle Tab navigation
                if (e.key === 'Tab') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleTabNavigation(e.shiftKey);
                  return;
                }
                
                // Handle Escape to cancel/close
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handleCancel();
                  return;
                }
                
                // Handle ⌘↵ (Command + Enter) to save note
                if (e.metaKey && e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                  return;
                }
                
                // Handle ⌘I (Command + I) to insert timestamp
                if (e.metaKey && !e.shiftKey && (e.key === 'i' || e.key === 'I')) {
                  e.preventDefault();
                  // Check if the editor is currently focused
                  const isEditorFocused = document.activeElement === editorRef.current;
                  if (isEditorFocused) {
                    insertTimestamp();
                  } else {
                    insertTimestampAtEnd();
                  }
                  return;
                }

                // Handle ⇧⌘I (Shift + Command + I) to insert textstamp
                if (e.metaKey && e.shiftKey && (e.key === 'i' || e.key === 'I')) {
                  e.preventDefault();
                  e.stopPropagation();
                  // Check if the editor is currently focused
                  const isEditorFocused = document.activeElement === editorRef.current;
                  if (isEditorFocused) {
                    createInlineTextStampInput();
                  } else {
                    insertChordAtEnd();
                  }
                  return;
                }
              }}
            >
              <Box
                ref={positionMarkerRef}
                position="absolute"
                top={0}
                bottom={0}
                width="3px"
                bg="accent.500"
                zIndex={10}
                style={{ left: '0%' }}
              />
            </Box>
            <HStack mt={1.5} px={2} position="relative">
              <Box w="full" textAlign="center">
                <Text 
                  fontSize="xs" 
                  color="gray.400"
                >
                  {formatTime(currentTime)} / {formatTime(duration)}
                </Text>
              </Box>
            </HStack>
            <HStack spacing={2} justify="center">
              <Tooltip 
                label={
                  <VStack spacing={1} align="start" color="gray.800">
                    <Text>Rewind 10 seconds</Text>
                    <Text>Hold to return to start</Text>
                  </VStack>
                }
                placement="top"
                hasArrow
                isDisabled={isMobile}
              >
                <IconButton
                  aria-label="Rewind 10s"
                  icon={<FaFastBackward />}
                  onClick={handleFastRewindClick}
                  onMouseDown={handleFastRewindStart}
                  onMouseUp={handleFastRewindEnd}
                  onMouseLeave={handleFastRewindEnd}
                  onTouchStart={handleFastRewindStart}
                  onTouchEnd={handleFastRewindEnd}
                  variant="ghost"
                  colorScheme="accent"
                  tabIndex={-1}
                />
              </Tooltip>
              <Tooltip label="Rewind 3s" isDisabled={isMobile}>
                <IconButton
                  aria-label="Rewind 3s"
                  icon={<FaStepBackward />}
                  onClick={() => handleSeek(-3)}
                  variant="ghost"
                  colorScheme="accent"
                  tabIndex={-1}
                />
              </Tooltip>
              <Tooltip label="Play/Pause" isDisabled={isMobile}>
                <IconButton
                  aria-label="Play/Pause"
                  icon={isPlaying ? <FaPause /> : <FaPlay />}
                  onClick={handlePlayPause}
                  variant="ghost"
                  colorScheme="accent"
                  size="lg"
                  tabIndex={-1}
                />
              </Tooltip>
              <Tooltip label="Forward 3s" isDisabled={isMobile}>
                <IconButton
                  aria-label="Forward 3s"
                  icon={<FaStepForward />}
                  onClick={() => handleSeek(3)}
                  variant="ghost"
                  colorScheme="accent"
                  tabIndex={-1}
                />
              </Tooltip>
              <Tooltip label="Forward 10s" isDisabled={isMobile}>
                <IconButton
                  aria-label="Forward 10s"
                  icon={<FaFastForward />}
                  onClick={() => handleSeek(10)}
                  variant="ghost"
                  colorScheme="accent"
                  tabIndex={-1}
                />
              </Tooltip>
            </HStack>

            <Box>
              <HStack spacing={2} mb={2}>
                <Tooltip 
                  label={<HStack spacing={2} color="gray.800"><Text>Timestamp</Text><TooltipKeyBox>⌘I</TooltipKeyBox></HStack>}
                  isDisabled={isMobile}
                >
                  <Button
                    size="sm"
                    colorScheme="accent"
                    variant="outline"
                    onClick={() => insertTimestamp()}
                    tabIndex={-1}
                  >
                    Timestamp
                  </Button>
                </Tooltip>
                <Tooltip 
                  label={<HStack spacing={2} color="gray.800"><Text>Textstamp</Text><TooltipKeyBox>⇧⌘I</TooltipKeyBox></HStack>}
                  isDisabled={isMobile}
                >
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    onClick={createInlineTextStampInput}
                    tabIndex={-1}
                  >
                    Textstamp
                  </Button>
                </Tooltip>
              </HStack>
              <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                minH="200px"
                p={3}
                bg="whiteAlpha.50"
                borderRadius="md"
                border="1px solid"
                borderColor="whiteAlpha.200"
                _focus={{ outline: 'none', borderColor: 'accent.500' }}
                className="editor"
                onClick={handleCursorPositioning}
                onKeyDown={(e) => {
                  // Handle Tab navigation
                  if (e.key === 'Tab') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleTabNavigation(e.shiftKey);
                    return;
                  }

                  // Prevent space and other hotkeys from triggering global handlers
                  e.stopPropagation();

                  // Handle ⌘↵ (Command + Enter) to save note
                  if (e.metaKey && e.key === 'Enter') {
                    e.preventDefault();
                    handleSave();
                    return;
                  }

                  // Handle ⌘I (Command + I) to insert timestamp
                  if (e.metaKey && !e.shiftKey && (e.key === 'i' || e.key === 'I')) {
                    e.preventDefault();
                    insertTimestamp();
                    return;
                  }

                  // Handle ⇧⌘I (Shift + Command + I) to insert textstamp
                  if (e.metaKey && e.shiftKey && (e.key === 'i' || e.key === 'I')) {
                    e.preventDefault();
                    e.stopPropagation();
                    createInlineTextStampInput();
                    return;
                  }

                  // Handle Escape to cancel/close
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    handleCancel();
                    return;
                  }

                  // Handle cursor positioning after arrow key navigation
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    setTimeout(handleCursorPositioning, 0);
                  }

                  // Handle timestamp and chord deletion
                  if (e.key === 'Delete' || e.key === 'Backspace') {
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                      const range = selection.getRangeAt(0);
                      // Check if cursor is adjacent to a timestamp or chord
                      let elementToDelete = null;
                      if (e.key === 'Delete') {
                        // For Delete key, check the element after the cursor
                        const nextNode = range.endContainer.childNodes[range.endOffset];
                        if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE && 
                            ((nextNode as Element).classList.contains('timestamp') || 
                             (nextNode as Element).classList.contains('chord'))) {
                          elementToDelete = nextNode;
                        }
                      } else if (e.key === 'Backspace') {
                        // For Backspace key, check the element before the cursor
                        const prevNode = range.startContainer.childNodes[range.startOffset - 1];
                        if (prevNode && prevNode.nodeType === Node.ELEMENT_NODE && 
                            ((prevNode as Element).classList.contains('timestamp') || 
                             (prevNode as Element).classList.contains('chord'))) {
                          elementToDelete = prevNode;
                        }
                      }
                      // Also check if a timestamp or chord is currently selected
                      if (!elementToDelete && !range.collapsed) {
                        const commonAncestor = range.commonAncestorContainer;
                        if (commonAncestor.nodeType === Node.ELEMENT_NODE) {
                          const selectedElements = (commonAncestor as Element).querySelectorAll('.timestamp, .chord');
                          for (const element of selectedElements) {
                            if (selection.containsNode(element, true)) {
                              elementToDelete = element;
                              break;
                            }
                          }
                        }
                      }
                      if (elementToDelete) {
                        e.preventDefault();
                        elementToDelete.remove();
                        // Rebuild cache when timestamp/chord is deleted
                        rebuildTimestampCache();
                        // Trigger input event to update state
                        const event = new Event('input', { bubbles: true });
                        editorRef.current?.dispatchEvent(event);
                      }
                    }
                  }
                }}
                onInput={(e) => {
                  const content = e.currentTarget.innerHTML;
                  console.log('Editor content changed:', content);
                  
                  // Note: Cache is only rebuilt when timestamps are actually added/edited,
                  // not on every keystroke. This is more efficient since regular text
                  // changes don't affect the timestamp cache.
                  
                  // Calculate live character count
                  const newLiveCount = calculateLiveCharCount(content);
                  setLiveCharCount(newLiveCount);
                  
                  // Calculate plain text length for the new content
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = content;
                  const newPlainText = tempDiv.textContent || tempDiv.innerText || '';
                  
                  // Calculate plain text length for the current saved note
                  const currentSavedNote = fileId ? getNote(fileId) : '';
                  tempDiv.innerHTML = currentSavedNote;
                  const currentPlainText = tempDiv.textContent || tempDiv.innerText || '';
                  
                  // Calculate the difference in characters
                  const charDifference = newPlainText.length - currentPlainText.length;
                  
                  // Check if the change would exceed global limit
                  if (charDifference > 0 && !canAddCharacters(charDifference)) {
                    // If exceeding limit, revert to previous content and restore previous count
                    e.currentTarget.innerHTML = note;
                    setLiveCharCount(calculateLiveCharCount(note));
                  } else {
                    setNote(content);
                  }
                }}
                _empty={{ _before: { content: '"Add your notes here..."', color: 'gray.500' } }}
              />
              <Text fontSize="sm" color="gray.500" textAlign="right" mt={1}>
                {liveCharCount}/{GLOBAL_CHARACTER_LIMIT} characters
              </Text>
            </Box>

            <HStack justify="flex-end">
              <Button variant="outline" border="none" onClick={handleCancel} tabIndex={-1}>
                Cancel
              </Button>
              <Tooltip label={<HStack spacing={2} color="gray.800"><Text>Save Note</Text><TooltipKeyBox>⌘↵</TooltipKeyBox></HStack>} isDisabled={isMobile}>
                <Button colorScheme="accent" onClick={handleSave} tabIndex={-1}>
                  Save Note
                </Button>
              </Tooltip>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NotesModal; 